import axios from 'axios';

const LOG_TAG = '[AIPromptEngineer]';
const isDevelopment = process.env.NODE_ENV === 'development';

function summarizeConfig(promptEngineerConfig) {
    return {
        source: promptEngineerConfig?.source || '',
        version: promptEngineerConfig?.version || '',
        industry: promptEngineerConfig?.business?.industry || '',
        audience: promptEngineerConfig?.business?.audience || '',
        additionalContextLength: promptEngineerConfig?.business?.additionalContext?.trim?.().length || 0,
        mainJob: promptEngineerConfig?.role?.mainJob || '',
        businessDescriptionLength: promptEngineerConfig?.business?.description?.trim?.().length || 0,
        selectedBehaviorsCount: promptEngineerConfig?.behavior?.selectedBehaviors?.length || 0,
        tasksCount: promptEngineerConfig?.role?.tasks?.length || 0,
        businessActionSelectionsCount: promptEngineerConfig?.businessActionSelections?.length || 0,
        customTasksLength: promptEngineerConfig?.role?.customTasks?.trim?.().length || 0,
        goalsCount: promptEngineerConfig?.success?.goals?.length || 0,
        prohibitedActionsCount: promptEngineerConfig?.success?.prohibitedActions?.length || 0,
        escalationTriggersCount: promptEngineerConfig?.success?.escalationTriggers?.length || 0
    };
}

function devLog(eventName, payload) {
    if (!isDevelopment) return;
    console.log(LOG_TAG, eventName, payload);
}

export async function generatePromptInstructions(promptEngineerConfig, generationMetadata = {}) {
    devLog('generation_request_started', summarizeConfig(promptEngineerConfig));

    try {
        const generationSessionKey = (generationMetadata?.generationSessionKey || '').trim();
        const response = await axios.post('/prompt-engineer/generate-instructions', {
            promptEngineerConfig,
            generationSessionKey,
            generationMetadata
        });

        if (!response?.data?.success || !response?.data?.instructions) {
            const message = response?.data?.message || 'Instruction generation failed.';
            throw new Error(message);
        }

        devLog('generation_request_succeeded', {
            ...summarizeConfig(promptEngineerConfig),
            generatedInstructionLength: response.data.instructions.length
        });

        return {
            instructions: response.data.instructions,
            generationSessionKey: response.data.generationSessionKey || generationMetadata?.generationSessionKey || '',
            generationSession: response.data.generationSession || null
        };
    } catch (error) {
        devLog('generation_request_failed', {
            ...summarizeConfig(promptEngineerConfig),
            message: error?.message || 'Instruction generation failed.'
        });
        throw error;
    }
}

export async function getPromptEngineerGenerationSession(generationSessionKey) {
    const normalizedSessionKey = (generationSessionKey || '').trim();
    if (!normalizedSessionKey) {
        throw new Error('generationSessionKey is required.');
    }

    try {
        const response = await axios.get(`/prompt-engineer/generation-sessions/${encodeURIComponent(normalizedSessionKey)}`);
        if (!response?.data?.success || !response?.data?.generationSession) {
            const message = response?.data?.message || 'Generation session lookup failed.';
            throw new Error(message);
        }

        return response.data.generationSession;
    } catch (error) {
        throw error;
    }
}

export async function materializePromptEngineerGenerationSession(generationSessionKey, payload = {}) {
    const normalizedSessionKey = (generationSessionKey || '').trim();
    if (!normalizedSessionKey) {
        throw new Error('generationSessionKey is required.');
    }

    try {
        const response = await axios.post(
            `/prompt-engineer/generation-sessions/${encodeURIComponent(normalizedSessionKey)}/materialize`,
            payload
        );

        if (!response?.data?.success || !response?.data?.instruction) {
            const message = response?.data?.message || 'Generation session materialization failed.';
            throw new Error(message);
        }

        return {
            instruction: response.data.instruction,
            generationSession: response.data.generationSession || null,
            saveIntent: response.data.saveIntent || ''
        };
    } catch (error) {
        throw error;
    }
}

export async function publishPromptEngineerDraft(instructionId) {
    if (!instructionId) {
        throw new Error('instructionId is required.');
    }

    try {
        const response = await axios.post('/instructions/publishPromptEngineerDraft', {
            id: instructionId
        });

        if (!response?.data?.success || !response?.data?.instruction) {
            const message = response?.data?.message || response?.data?.error || 'Draft publish failed.';
            throw new Error(message);
        }

        return {
            instruction: response.data.instruction,
            retiredDraftId: response.data.retiredDraftId || null,
            cleanupRequired: Boolean(response.data.cleanupRequired),
            cleanupWarning: response.data.cleanupWarning || ''
        };
    } catch (error) {
        throw error;
    }
}

export async function getBusinessActionInterviewMetadata() {
    devLog('business_action_metadata_request_started', {});

    try {
        const response = await axios.get('/prompt-engineer/business-actions');
        if (!response?.data?.success || !response?.data?.metadata) {
            const message = response?.data?.message || 'Business action metadata request failed.';
            throw new Error(message);
        }

        devLog('business_action_metadata_request_succeeded', {
            hasEligibleConnectedServices: Boolean(response.data.metadata?.hasEligibleConnectedServices),
            businessActionsCount: Array.isArray(response.data.metadata?.businessActions)
                ? response.data.metadata.businessActions.length
                : 0
        });

        return response.data.metadata;
    } catch (error) {
        devLog('business_action_metadata_request_failed', {
            message: error?.message || 'Business action metadata request failed.'
        });
        throw error;
    }
}

export async function generateBusinessDescription(promptEngineerConfig, sourceUrl = '', mode = 'context') {
    const trimmedSourceUrl = (sourceUrl || '').trim();
    const requestMode = mode === 'url' && trimmedSourceUrl ? 'url' : 'context';

    devLog('business_description_generation_started', {
        ...summarizeConfig(promptEngineerConfig),
        mode: requestMode,
        hasSourceUrl: Boolean(trimmedSourceUrl)
    });

    try {
        const response = await axios.post('/prompt-engineer/generate-business-description', {
            promptEngineerConfig,
            sourceUrl: trimmedSourceUrl,
            mode: requestMode
        });

        if (!response?.data?.success || !response?.data?.description) {
            const message = response?.data?.message || 'Business description generation failed.';
            throw new Error(message);
        }

        devLog('business_description_generation_succeeded', {
            ...summarizeConfig(promptEngineerConfig),
            mode: requestMode,
            generatedDescriptionLength: response.data.description.length,
            sourceType: response?.data?.metadata?.sourceType || ''
        });

        return {
            description: response.data.description,
            metadata: response.data.metadata || {}
        };
    } catch (error) {
        devLog('business_description_generation_failed', {
            ...summarizeConfig(promptEngineerConfig),
            mode: requestMode,
            message: error?.message || 'Business description generation failed.'
        });
        throw error;
    }
}
